import React from 'react';
import { Alert, Box, Chip, Typography } from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { FONT_ENUM } from '../../types/global/fonts.types';
import type { ICampaignParticipationGate } from '../../types/global/campaignParticipationGate.types';
import { getParticipationGateBannerCopy } from '../../utils/campaignParticipationGate';

const GOLD = '#ffc965';

export interface CampaignParticipationGateBannerProps {
    gate: ICampaignParticipationGate;
    /** 'creator' uses gold accent; 'brand' could be extended later */
    variant?: 'creator' | 'brand';
    showFootnote?: boolean;
    sx?: object;
}

export function CampaignParticipationGateBanner({
    gate,
    variant = 'creator',
    showFootnote = variant === 'creator',
    sx,
}: CampaignParticipationGateBannerProps) {
    const { chipLabel: chip, title, body, footnote } = getParticipationGateBannerCopy(gate);
    const accent = variant === 'creator' ? GOLD : '#00fdc1';

    return (
        <Alert
            severity="warning"
            icon={<PauseCircleOutlineIcon sx={{ color: accent }} />}
            sx={{
                mb: 2,
                bgcolor: 'rgba(255, 193, 7, 0.08)',
                border: `1px solid ${accent}44`,
                color: '#fff',
                alignItems: 'flex-start',
                '& .MuiAlert-message': { width: '100%' },
                ...sx,
            }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 15, color: '#fff' }}>{title}</Typography>
                {chip ? (
                    <Chip
                        label={chip}
                        size="small"
                        sx={{
                            height: 22,
                            fontSize: 10,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            bgcolor: 'rgba(255,255,255,0.08)',
                            color: accent,
                            border: `1px solid ${accent}55`,
                        }}
                    />
                ) : null}
            </Box>
            <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>{body}</Typography>
            {showFootnote && footnote ? (
                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', mt: 1 }}>{footnote}</Typography>
            ) : null}
        </Alert>
    );
}
